import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useGetActivityFeedQuery, useGetAvailableFeedQuery } from 'store/services/activity/activityApi';
import { useActivitySocket } from './useActivitySocket';
import { Activity, ActivityType } from 'types/interfaces/activity';
import type { SearchRequestArgs } from 'screens/Main/Search/searchState';
import { ALL_ACTIVITY_TYPES, DEFAULT_ACTIVE_TYPES, FILTER_DISPLAY_ORDER } from 'screens/Main/tabs/Activity/constants';

const FEED_PAGE_SIZE = 30;
const SCROLL_THRESHOLD = 200; // px from top before considered "scrolled down"
const FILTER_STORAGE_KEY = 'activity-filter-types';
// Quiet window before a burst of Affordable-feed socket signals triggers one refetch
const AVAILABLE_REFETCH_DEBOUNCE_MS = 700;

/** Load the saved filter from localStorage, falling back to the default.
 *  Single-select: only the first valid saved type is honored (older builds may have
 *  persisted several). The active set always holds exactly one type. */
const getInitialActiveTypes = (): Set<ActivityType> => {
  try {
    const saved = localStorage.getItem(FILTER_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as ActivityType[];
      const first = Array.isArray(parsed)
        ? parsed.find(t => FILTER_DISPLAY_ORDER.includes(t))
        : undefined;
      if (first) return new Set([first]);
    }
  } catch { /* ignore corrupt data */ }
  return new Set(DEFAULT_ACTIVE_TYPES);
};

/** Transform an AvailableFeedItem into the unified Activity shape.
 *  Handles both the dedicated AvailableFeedItem shape and pre-formatted Activity objects. */
const toActivity = (item: any): Activity => {
  // If the backend already returns Activity-shaped objects, pass through
  if (item.type === 'available' && item.id && item.card && item.user && item.createdAt) {
    return {
      ...item,
      // Ensure wantEntryId is present
      wantEntryId: item.wantEntryId ?? undefined,
      // Ensure user has points field
      user: {
        ...item.user,
        points: item.user.points ?? null,
        countryCode: item.user.countryCode ?? item.user.country ?? null,
      },
      // Ensure card has collectorNumber
      card: {
        ...item.card,
        collectorNumber: item.card.collectorNumber ?? null,
      },
    };
  }

  // AvailableFeedItem shape → Activity
  return {
    id: `available_${item.wantEntryId}_0`,
    type: 'available' as const,
    createdAt: item.timestamp ?? item.createdAt,
    user: {
      id: item.user?.id,
      username: item.user?.username ?? '',
      avatarUrl: item.user?.avatarUrl ?? null,
      countryCode: item.user?.countryCode ?? item.user?.country ?? null,
      points: item.user?.points ?? null,
      isElite: item.user?.isElite,
      showAnimatedBorder: item.user?.showAnimatedBorder,
      borderColor1: item.user?.borderColor1,
      borderColor2: item.user?.borderColor2,
    },
    card: {
      id: item.card?.id,
      name: item.card?.name ?? '',
      scryfallId: item.card?.scryfallId ?? '',
      imageUrl: item.card?.imageUrl ?? '',
      setCode: item.card?.setCode ?? '',
      collectorNumber: item.card?.collectorNumber ?? null,
      foil: item.card?.foil ?? false,
    },
    pointValue: item.card?.pointValue ?? item.pointValue ?? null,
    wantEntryId: item.wantEntryId,
  };
};

interface UseActivityFeedOptions {
  enableSocket?: boolean;
  pollingInterval?: number;
  /**
   * Card-attribute filters (the shared FilterPanel). Applied server-side to
   * both feeds and registered on the socket, so a live event that does not
   * match is never pushed (decision 9). A change restarts pagination and drops
   * anything buffered under the previous filters.
   */
  filters?: SearchRequestArgs;
}

interface UseActivityFeedReturn {
  /** Visible activities (filtered, respecting pause state) */
  activities: Activity[];
  /** Whether the initial data is loading */
  isLoading: boolean;
  /** Whether more pages are available */
  hasMore: boolean;
  /** Load next page of historical activity */
  loadMore: () => void;
  /** Whether the feed is paused */
  isPaused: boolean;
  /** Toggle pause/resume */
  togglePause: () => void;
  /** Number of pending items while paused */
  pendingCount: number;
  /** Currently active filter types (single-select — always exactly one) */
  activeTypes: Set<ActivityType>;
  /** Select a single activity type filter (replaces the previously active one) */
  selectType: (type: ActivityType) => void;
  /** Whether the WebSocket is connected */
  isSocketConnected: boolean;
  /** IDs of items that should animate on entry */
  newItemIds: Set<string>;
  /** Mark an item as having finished its entry animation */
  clearNewItem: (id: string) => void;
  /** Whether we're loading more pages */
  isFetchingMore: boolean;
  /** Number of items buffered while scrolled down */
  scrolledPendingCount: number;
  /** Flush scroll-buffered items into the feed with animation */
  showBuffered: () => void;
  /** Scroll event handler — bind to the scroll container */
  onScroll: (e: React.UIEvent<HTMLElement>) => void;
  /** Trader point-balance filter (inclusive). undefined = unset. */
  traderBalanceMin: number | undefined;
  traderBalanceMax: number | undefined;
  setTraderBalanceMin: (value: number | undefined) => void;
  setTraderBalanceMax: (value: number | undefined) => void;
  /** The 30-day lookback cap ended the walk (card filters active); the feed says so instead of "load more". */
  lookbackCapped: boolean;
}

export const useActivityFeed = (
  options: UseActivityFeedOptions = {}
): UseActivityFeedReturn => {
  const {
    enableSocket = true,
    pollingInterval = 60000,
    filters,
  } = options;

  // Filter state — loaded from localStorage or defaults to Available + Wanted
  const [activeTypes, setActiveTypes] = useState<Set<ActivityType>>(getInitialActiveTypes);

  // Persist filter selection to localStorage
  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(Array.from(activeTypes)));
  }, [activeTypes]);

  // Pause state
  const [isPaused, setIsPaused] = useState(false);
  const bufferedIdsRef = useRef<Set<string>>(new Set());
  const [pendingCount, setPendingCount] = useState(0);

  // Scroll-aware buffering state
  const isScrolledDownRef = useRef(false);
  const scrollBufferedIdsRef = useRef<Set<string>>(new Set());
  const [scrolledPendingCount, setScrolledPendingCount] = useState(0);

  // Track new items for entry animation
  const [newItemIds, setNewItemIds] = useState<Set<string>>(new Set());

  // Pagination cursor for load more. Shared by both feeds (recent + available); whichever
  // feed is active drives it. `cursorRef` mirrors it for the (stale-closure-free) socket handler.
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const cursorRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  // Trader point-balance filter (debounced, like the Trade Alerts price filter). A filter
  // change restarts pagination since it produces a fresh server-side feed.
  const [traderBalanceMin, setTraderBalanceMin] = useState<number | undefined>(undefined);
  const [traderBalanceMax, setTraderBalanceMax] = useState<number | undefined>(undefined);
  const [debouncedBalanceMin, setDebouncedBalanceMin] = useState<number | undefined>(undefined);
  const [debouncedBalanceMax, setDebouncedBalanceMax] = useState<number | undefined>(undefined);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedBalanceMin(traderBalanceMin);
      setDebouncedBalanceMax(traderBalanceMax);
      setCursor(undefined);
    }, 300);
    return () => clearTimeout(timer);
  }, [traderBalanceMin, traderBalanceMax]);

  // Socket connection state
  const isSocketConnected = useSelector(
    (state: RootState) => state.activity.isConnected
  );

  // The Available ("Affordable") pill is the only computed feed; it's fetched,
  // not cache-patched like the event-type filters.
  const isAvailableActive = activeTypes.has('available');

  // Debounced reset-to-first-page of the computed Affordable feed in response to
  // socket signals (want add/delete, balance change). Because the feed is a
  // server-computed, paginated snapshot, we re-pull a fresh first page rather than
  // patch the cache. A burst of signals coalesces into a single refetch after
  // ~700ms of quiet, and nothing runs unless the Affordable view is active.
  // `refetchAvailable` is captured into a ref below (the query hook is declared after this).
  const refetchAvailableRef = useRef<(() => void) | null>(null);
  const availableSignalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetAvailableToFirstPage = useCallback(() => {
    // Exactly one fetch path: if a cursor is set, clearing it changes the query args and
    // refetches page 1 (merge replaces the cache). If already at page 1, force a refetch.
    // Doing both would race a stale-cursor append against the page-1 replace.
    if (cursorRef.current) {
      setCursor(undefined);
    } else {
      refetchAvailableRef.current?.();
    }
  }, []);
  const handleAvailableSignal = useCallback(() => {
    if (!isAvailableActive) return;
    if (availableSignalTimerRef.current) {
      clearTimeout(availableSignalTimerRef.current);
    }
    availableSignalTimerRef.current = setTimeout(() => {
      resetAvailableToFirstPage();
    }, AVAILABLE_REFETCH_DEBOUNCE_MS);
  }, [isAvailableActive, resetAvailableToFirstPage]);
  // Clear any pending debounce on unmount
  useEffect(() => () => {
    if (availableSignalTimerRef.current) {
      clearTimeout(availableSignalTimerRef.current);
    }
  }, []);

  // Handle new activity from WebSocket
  const handleActivity = useCallback(
    (activity: Activity) => {
      if (isPaused) {
        // Buffer the item — it's already in RTK cache but we track it
        bufferedIdsRef.current.add(activity.id);
        setPendingCount(bufferedIdsRef.current.size);
      } else if (isScrolledDownRef.current) {
        // User is scrolled down — buffer for the pill
        scrollBufferedIdsRef.current.add(activity.id);
        setScrolledPendingCount(scrollBufferedIdsRef.current.size);
      } else {
        // Mark as new for entry animation
        setNewItemIds(prev => {
          const next = new Set(prev);
          next.add(activity.id);
          return next;
        });
      }
    },
    [isPaused]
  );

  // Card filters changed: the previous page set and anything buffered under
  // the old filters are stale. Restart pagination and empty both buffers.
  useEffect(() => {
    setCursor(undefined);
    bufferedIdsRef.current = new Set();
    setPendingCount(0);
    scrollBufferedIdsRef.current = new Set();
    setScrolledPendingCount(0);
  }, [filters]);

  // Initialize socket
  useActivitySocket({
    enabled: enableSocket,
    onActivity: handleActivity,
    onAvailableSignal: handleAvailableSignal,
    filters,
  });

  // Fetch feed data
  const shouldPoll = !isSocketConnected;
  const {
    data,
    isLoading,
    isFetching,
  } = useGetActivityFeedQuery(
    {
      limit: FEED_PAGE_SIZE,
      cursor,
      traderBalanceMin: debouncedBalanceMin,
      traderBalanceMax: debouncedBalanceMax,
      filters,
    },
    {
      pollingInterval: shouldPoll ? pollingInterval : 0,
      refetchOnMountOrArgChange: true,
      // The Affordable pill renders the bounded /activity/available snapshot only; the
      // recent feed contributes nothing visible there, so skip it to fully decouple the
      // two feeds (and avoid the runaway loadMore loop driven by recent-feed hasMore).
      skip: isAvailableActive,
    }
  );

  // Available feed — only fetched when the Available pill is active. Socket
  // signals drive near-real-time refetches (see handleAvailableSignal); the
  // polling interval remains as a safety net for missed events / dropped sockets.
  const {
    data: availableData,
    isLoading: availableIsLoading,
    isFetching: availableIsFetching,
    refetch: refetchAvailable,
  } = useGetAvailableFeedQuery(
    {
      hours: 24,
      cursor,
      traderBalanceMin: debouncedBalanceMin,
      traderBalanceMax: debouncedBalanceMax,
      filters,
    },
    { skip: !isAvailableActive, pollingInterval: 120000, refetchOnMountOrArgChange: true }
  );
  // Expose the latest refetch to the (earlier-declared) debounced signal handler.
  useEffect(() => {
    refetchAvailableRef.current = refetchAvailable;
  }, [refetchAvailable]);

  // Reconnect catch-up: when the socket comes back while viewing the Affordable
  // feed, refetch once to recover any signals missed while disconnected.
  const wasSocketConnectedRef = useRef(isSocketConnected);
  useEffect(() => {
    if (isSocketConnected && !wasSocketConnectedRef.current && isAvailableActive) {
      resetAvailableToFirstPage();
    }
    wasSocketConnectedRef.current = isSocketConnected;
  }, [isSocketConnected, isAvailableActive, resetAvailableToFirstPage]);

  // All activities from cache
  const allActivities = useMemo(() => data?.activities ?? [], [data]);
  // Both feeds paginate now: read the active feed's real has-more. The Affordable feed returns a
  // genuine nextCursor/hasMore (keyset over effective-recency), so the infinite-scroll observer
  // loads real pages and stops cleanly at hasMore=false — no perpetual-spinner loop.
  const hasMore = isAvailableActive
    ? (availableData?.hasMore ?? false)
    : (data?.hasMore ?? false);
  const lookbackCapped = isAvailableActive
    ? (availableData?.lookbackCapped ?? false)
    : (data?.lookbackCapped ?? false);

  // Transform available items into Activity shape
  const availableActivities = useMemo(() => {
    if (!isAvailableActive || !availableData?.items) return [];
    return availableData.items.map(toActivity);
  }, [isAvailableActive, availableData]);

  // Apply filters, pause buffering, scroll buffering, and merge available feed
  const activities = useMemo(() => {
    // Affordable feed: render the server's paginated list verbatim, in its effective-recency
    // order (GREATEST(want.createdAt, owner.pointsLastChangedAt) DESC + want-id tiebreaker).
    // Re-sorting by createdAt here would diverge from the server order and reorder/interleave
    // items across page boundaries, breaking the keyset cursor's stability. The recent feed is
    // skipped under Affordable, so there's nothing else to merge in.
    if (isAvailableActive) {
      return availableActivities;
    }

    let filtered = allActivities;

    // Hide buffered items when paused
    if (isPaused && pendingCount > 0) {
      filtered = filtered.filter(a => !bufferedIdsRef.current.has(a.id));
    }

    // Hide scroll-buffered items when scrolled down
    if (scrolledPendingCount > 0) {
      filtered = filtered.filter(a => !scrollBufferedIdsRef.current.has(a.id));
    }

    // Apply type filter for WebSocket-driven types
    const wsActiveTypes = ALL_ACTIVITY_TYPES.filter(t => activeTypes.has(t));
    if (wsActiveTypes.length < ALL_ACTIVITY_TYPES.length) {
      filtered = filtered.filter(a => activeTypes.has(a.type));
    }

    return filtered;
  }, [allActivities, isPaused, activeTypes, pendingCount, scrolledPendingCount, isAvailableActive, availableActivities]);

  // Load more historical activities — advance the active feed's cursor.
  const loadMore = useCallback(() => {
    const nextCursor = isAvailableActive ? availableData?.nextCursor : data?.nextCursor;
    const fetching = isAvailableActive ? availableIsFetching : isFetching;
    if (nextCursor && !fetching) {
      setCursor(nextCursor);
    }
  }, [isAvailableActive, availableData?.nextCursor, data?.nextCursor, availableIsFetching, isFetching]);

  // Toggle pause/resume
  const togglePause = useCallback(() => {
    setIsPaused(prev => {
      if (prev) {
        // Resuming — mark all buffered items as new for animation
        const buffered = bufferedIdsRef.current;
        if (buffered.size > 0) {
          setNewItemIds(prevNew => {
            const next = new Set(prevNew);
            buffered.forEach(id => next.add(id));
            return next;
          });
        }
        bufferedIdsRef.current = new Set();
        setPendingCount(0);
      }
      return !prev;
    });
  }, []);

  // Select an activity type filter (single-select). Activating a pill replaces the
  // active filter; exactly one is active at all times, so re-clicking the active pill
  // is a no-op. No two filters can be active together, so categories can't overlap.
  const selectType = useCallback((type: ActivityType) => {
    // Reset pagination on every feed switch: the single `cursor` state is shared by the recent
    // and available feeds, so a leftover cursor from one feed must not leak into the other.
    setCursor(undefined);
    setActiveTypes(new Set([type]));
  }, []);

  // Clear new item animation flag
  const clearNewItem = useCallback((id: string) => {
    setNewItemIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Scroll handler — tracks whether user is scrolled down
  const onScroll = useCallback((e: React.UIEvent<HTMLElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    const wasScrolledDown = isScrolledDownRef.current;
    isScrolledDownRef.current = scrollTop > SCROLL_THRESHOLD;

    // If user scrolled back to top, auto-flush buffered items
    if (wasScrolledDown && !isScrolledDownRef.current && scrollBufferedIdsRef.current.size > 0) {
      const buffered = scrollBufferedIdsRef.current;
      setNewItemIds(prev => {
        const next = new Set(prev);
        buffered.forEach(id => next.add(id));
        return next;
      });
      scrollBufferedIdsRef.current = new Set();
      setScrolledPendingCount(0);
    }
  }, []);

  // Flush scroll-buffered items (called from "new items" pill click)
  const showBuffered = useCallback(() => {
    const buffered = scrollBufferedIdsRef.current;
    if (buffered.size > 0) {
      setNewItemIds(prev => {
        const next = new Set(prev);
        buffered.forEach(id => next.add(id));
        return next;
      });
      scrollBufferedIdsRef.current = new Set();
      setScrolledPendingCount(0);
    }
  }, []);

  return {
    activities,
    // Show the initial-load skeleton for whichever feed is active. The recent query is skipped
    // under Affordable (so its isLoading is unusable there); read the available query's instead.
    isLoading: isAvailableActive ? availableIsLoading : isLoading,
    hasMore,
    loadMore,
    isPaused,
    togglePause,
    pendingCount,
    activeTypes,
    selectType,
    isSocketConnected,
    newItemIds,
    clearNewItem,
    isFetchingMore: (isAvailableActive ? availableIsFetching : isFetching) && !!cursor,
    scrolledPendingCount,
    showBuffered,
    onScroll,
    traderBalanceMin,
    traderBalanceMax,
    setTraderBalanceMin,
    setTraderBalanceMax,
    lookbackCapped,
  };
};

export default useActivityFeed;
